import { Suspense } from "react";

import { ZoneParams } from "../api/zones";
import useWistiaScript from "../hooks/use-wistia-script";

import { CmsComponent, CmsComponentData, CmsExperiments, CmsComponentOptions } from "./cms-component";

export type CmsWidgetProps = {
  experiments?: string;
  zoneParams: ZoneParams;
  zone?: {
    components?: CmsComponentData[];
  };
};

export function CmsWidget({ experiments, zoneParams, zone }: CmsWidgetProps) {
  // inject wistia script for content blocks that contain videos
  useWistiaScript(zone?.components);

  if (!zone?.components?.length) {
    return null;
  }

  const defaultOptions: CmsComponentOptions = {
    nonSku: {
      horizontalPadding: "default",
      topPadding: "default",
      bottomPadding: "default",
    },
    sku: {
      horizontalPadding: "default",
      topPadding: "default",
      bottomPadding: "default",
    },
  };

  // Format for experiments: "experiment1:variation1,experiment2:variation2"
  // We need to parse this string into an array of objects
  const getExperiments = (experiments: string | undefined): CmsExperiments => {
    if (!experiments) {
      return [];
    }
    return experiments.split(",").map((experiment) => {
      const [name, variation] = experiment.split(":");
      return { name: name ?? "", variation: variation };
    });
  };

  const getCustomOptions = (type: string): CmsComponentOptions => {
    switch (type) {
      case "hp":
        // CAPO-39 Handle HP SKU
        return {
          ...defaultOptions,
          sku: {
            ...defaultOptions.sku,
            cardLayout: "portrait-small",
            experiments: [{ name: "sfwPromotionsShowDealCount" }],
          },
        } as CmsComponentOptions;
      case "general/types/thank-you": {
        // CAPO-48: Handle custom zone logic for Thank You page
        return {
          ...defaultOptions,
          banner: {
            fullWidth: false,
          },
          contentBlock: {
            horizontalPadding: "compact",
          },
        } as CmsComponentOptions;
      }
      default:
        return defaultOptions;
    }
  };

  const parsedExperiments = experiments ? getExperiments(experiments) : undefined;
  const customOptions = getCustomOptions(zoneParams.pageType);

  return (
    <Suspense>
      {zone.components.map((data, i) => (
        <CmsComponent
          key={i}
          data={data}
          options={customOptions}
          zoneParams={{
            pageType: zoneParams.pageType,
            selectionKey: zoneParams.selectionKey,
            zoneId: zoneParams.zoneId,
          }}
          experiments={parsedExperiments}
        />
      ))}
    </Suspense>
  );
}
